// Listing modes for Personnel — Dashboard / Roster List.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/blank/BlankLayout.tsx and src/users/user-management/modes.ts.
//
//   <base>/dashboard  → PersonnelOverview (the compliance & roster health dashboard)
//   <base>/overview   → redirect to <base>/dashboard — the mode's name until H94
//                        (Aaron 2026-09-28: no "overviews" at a module level)
//   <base>/list       → PersonnelDataGrid (the full working roster)
//   <base>/onboarding → PersonnelOnboarding (new hires and their stages;
//                        spec repo/brain/projects/mustang-personnel-onboarding-mode-design-2026-10-07.md)

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";
import { migrateModeSegment } from "../components/renamedModeSegments";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  dashboard: "Dashboard",
  list: "Roster",
  onboarding: "Onboarding",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  dashboard: "Compliance and roster health",
  list: "Staff and compliance RAG",
  onboarding: "New hires and stages",
};

export const DEFAULT_MODE_SEGMENT = "dashboard";

/**
 * `<module>.viewMode` — matches the shape used across converted modules.
 */
export const MODE_STORAGE_KEY = "personnel.viewMode";

/**
 * Is this a real mode segment?
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  } catch {
    return DEFAULT_MODE_SEGMENT;
  }
  stored = migrateModeSegment(stored); // "overview" (pre-H94) -> "dashboard"
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
